import dims from "./image-dims.json";

const files = import.meta.glob("../assets/img/*.{webp,avif}", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

/**
 * `sizes` that match the 84rem shell on a ~1350px desktop, not raw viewport
 * fractions (33vw of 1350 is wider than a 3-column card inside the shell).
 */
export const imgSizes = {
  hero: "100vw",
  half: "(min-width: 768px) min(46vw, 38rem), 100vw",
  third: "(min-width: 1024px) min(30vw, 25rem), (min-width: 640px) min(48vw, 36rem), 100vw",
  portrait: "(min-width: 1400px) 700px, (min-width: 1024px) min(40vw, 34rem), 100vw",
  marquee: "(min-width: 768px) 320px, 256px",
  lightbox: "(min-width: 1024px) min(80vw, 64rem), 100vw",
} as const;

type Variant = { url: string; w: number };

const variants: Record<string, Variant[]> = {};
const avifVariants: Record<string, Variant[]> = {};

function upsert(map: Record<string, Variant[]>, name: string, w: number, url: string, prefer: boolean) {
  const list = (map[name] ??= []);
  const existing = list.find((v) => v.w === w);
  if (existing) {
    if (prefer) existing.url = url;
    return;
  }
  list.push({ url, w });
}

for (const [path, url] of Object.entries(files)) {
  const base = path.split("/").pop()!;
  const match = /^(.*)-(\d+)w\.(webp|avif)$/i.exec(base);
  if (!match) continue;
  const name = match[1]!;
  const w = Number(match[2]);
  const ext = match[3]!.toLowerCase();
  if (ext === "avif") {
    upsert(avifVariants, name, w, url, true);
  } else {
    upsert(variants, name, w, url, true);
  }
}

for (const list of Object.values(variants)) list.sort((a, b) => a.w - b.w);
for (const list of Object.values(avifVariants)) list.sort((a, b) => a.w - b.w);

const dimsMap = dims as unknown as Record<string, Record<string, number[]>>;

/** Prefer a ~720px-wide file so the src fallback isn't a 2x desktop asset. */
function pickSrc(list: Variant[]): Variant {
  const TARGET = 720;
  let best = list[0]!;
  let bestDist = Math.abs(best.w - TARGET);
  for (const v of list) {
    const dist = Math.abs(v.w - TARGET);
    if (dist < bestDist || (dist === bestDist && v.w > best.w)) {
      best = v;
      bestDist = dist;
    }
  }
  return best;
}

function pickLargest(list: Variant[]): Variant {
  return list[list.length - 1]!;
}

function srcSet(list: Variant[] | undefined): string | undefined {
  if (!list?.length) return undefined;
  return list.map((v) => `${v.url} ${v.w}w`).join(", ");
}

/** Display URL for an image name (mid-size fallback; srcset supplies the rest). */
export function img(name: string): string {
  const list = variants[name];
  if (!list?.length) return "";
  return pickSrc(list).url;
}

/** Smallest available URL — used for placeholders/fallbacks. */
export function imgSmall(name: string): string {
  return variants[name]?.[0]?.url ?? "";
}

export function imgSrcSet(name: string): string | undefined {
  const list = variants[name];
  if (!list || list.length < 2) return undefined;
  return srcSet(list);
}

export function imgAvif(name: string): string {
  const list = avifVariants[name];
  if (!list?.length) return "";
  return pickSrc(list).url;
}

export function imgSrcSetAvif(name: string): string | undefined {
  return srcSet(avifVariants[name]);
}

/** Intrinsic pixel dimensions of the largest variant. */
export function imgDims(name: string): { width: number; height: number } | undefined {
  const entry = dimsMap[name];
  const list = variants[name];
  const largest = list?.[list.length - 1]?.w;
  if (!entry || largest === undefined) return undefined;
  const d = entry[String(largest)];
  return d && d[0] !== undefined && d[1] !== undefined ? { width: d[0], height: d[1] } : undefined;
}

/** Spread-ready props for a responsive <img>. */
export function imgProps(name: string, sizes: string = imgSizes.hero) {
  const srcSetStr = imgSrcSet(name);
  return {
    src: img(name),
    srcSet: srcSetStr,
    sizes: srcSetStr ? sizes : undefined,
    ...(imgDims(name) ?? {}),
  };
}

/**
 * Props for the LCP photo: AVIF srcset (no <picture>) so React 19 hoists a
 * matching <link rel="preload" fetchPriority="high"> and the painted file is
 * the same URL the head preload already named.
 */
export function lcpImgProps(name: string, sizes: string = imgSizes.hero) {
  const avifList = avifVariants[name];
  const webpList = variants[name];
  const list = avifList?.length ? avifList : webpList;
  if (!list?.length) return imgProps(name, sizes);
  const srcSetStr = list.length >= 2 ? srcSet(list) : undefined;
  return {
    src: pickLargest(list).url,
    srcSet: srcSetStr,
    sizes: srcSetStr ? sizes : undefined,
    ...(imgDims(name) ?? {}),
  };
}

/** Preload hints for the LCP image (AVIF when it is actually smaller). */
export function lcpPreload(name: string, sizes = imgSizes.hero) {
  const avifList = avifVariants[name];
  const webpList = variants[name];
  const avif = srcSet(avifList);
  if (avif && avifList?.length) {
    return {
      rel: "preload" as const,
      as: "image" as const,
      type: "image/avif",
      href: pickLargest(avifList).url,
      imageSrcSet: avif,
      imageSizes: sizes,
      fetchPriority: "high" as const,
    };
  }
  return {
    rel: "preload" as const,
    as: "image" as const,
    type: "image/webp",
    href: webpList?.length ? pickLargest(webpList).url : img(name),
    imageSrcSet: imgSrcSet(name),
    imageSizes: sizes,
    fetchPriority: "high" as const,
  };
}
